import { useState, useEffect } from "react";
import { usersAPI, jobsAPI, glAccountsAPI, vendorsAPI } from "../lib/api";
import { buildGlItems } from "../lib/glAccounts";
import { SearchableSelect } from "./SearchableSelect";
import { Button } from "./ui/button";
import { X } from "@phosphor-icons/react";

/**
 * Reusable filter bar for Transactions / Expenses / Approvals pages.
 *
 * Props:
 *  - value: { submitted_by, vendor, job_id, gl_account_id, card_last_four }
 *  - onChange: (next) => void
 *  - show: array of which filters to render, e.g. ["submitter","vendor","job","gl","card"]
 */
export function QuickFilters({ value, onChange, show = ["submitter", "vendor", "job", "gl", "card"] }) {
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    (async () => {
      const calls = [];
      if (show.includes("submitter") || show.includes("card")) calls.push(usersAPI.list().catch(() => ({ data: { users: [] } })));
      else calls.push(Promise.resolve({ data: { users: [] } }));
      if (show.includes("job")) calls.push(jobsAPI.list().catch(() => ({ data: { jobs: [] } })));
      else calls.push(Promise.resolve({ data: { jobs: [] } }));
      if (show.includes("gl")) calls.push(glAccountsAPI.list().catch(() => ({ data: { accounts: [] } })));
      else calls.push(Promise.resolve({ data: { accounts: [] } }));
      if (show.includes("vendor")) calls.push(vendorsAPI.listAll().catch(() => ({ data: { vendors: [] } })));
      else calls.push(Promise.resolve({ data: { vendors: [] } }));
      const [u, j, g, v] = await Promise.all(calls);
      setUsers(u.data.users || []);
      setJobs(j.data.jobs || []);
      setGlAccounts(g.data.accounts || []);
      setVendors(v.data.vendors || []);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (key, v) => onChange({ ...value, [key]: v });
  const hasAny = Object.values(value || {}).some((x) => x);

  // Aggregate all cards across all users into a flat list
  const allCards = [];
  users.forEach((u) => {
    (u.cards || []).forEach((c) => {
      if (c.last_four && !allCards.find((x) => x.last_four === c.last_four)) {
        allCards.push({ last_four: c.last_four, holder: u.name });
      }
    });
    if (u.card_last_four && !allCards.find((x) => x.last_four === u.card_last_four)) {
      allCards.push({ last_four: u.card_last_four, holder: u.name });
    }
  });

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="quick-filters">
      {show.includes("submitter") && (
        <div className="w-[180px]">
          <SearchableSelect
            testId="filter-submitter"
            size="sm"
            value={value.submitted_by || ""}
            onChange={(v) => update("submitted_by", v)}
            placeholder="Submitter"
            searchPlaceholder="Search people..."
            emptyText="No users."
            items={[
              { value: "", label: "All submitters" },
              ...users.map((u) => ({ value: u.id, label: u.name || u.email, searchText: u.email })),
            ]}
          />
        </div>
      )}
      {show.includes("vendor") && (
        <div className="w-[200px]">
          <SearchableSelect
            testId="filter-vendor"
            size="sm"
            value={value.vendor || ""}
            onChange={(v) => update("vendor", v)}
            placeholder="Vendor"
            searchPlaceholder="Search vendors..."
            emptyText="No vendors."
            items={[
              { value: "", label: "All vendors" },
              ...vendors.map((v) => ({ value: v.name, label: v.name })),
            ]}
          />
        </div>
      )}
      {show.includes("job") && (
        <div className="w-[180px]">
          <SearchableSelect
            testId="filter-job"
            size="sm"
            value={value.job_id || ""}
            onChange={(v) => update("job_id", v)}
            placeholder="Job"
            searchPlaceholder="Search jobs..."
            emptyText="No jobs."
            items={[
              { value: "", label: "All jobs" },
              ...jobs.map((j) => ({
                value: j.id,
                label: j.name,
                searchText: `${j.number || ""} ${j.customer || ""}`,
              })),
            ]}
          />
        </div>
      )}
      {show.includes("gl") && (
        <div className="w-[200px]">
          <SearchableSelect
            testId="filter-gl"
            size="sm"
            value={value.gl_account_id || ""}
            onChange={(v) => update("gl_account_id", v)}
            placeholder="Overhead account"
            searchPlaceholder="Search overhead accounts..."
            emptyText="No overhead accounts."
            wrap
            items={[
              { value: "", label: "All overhead accounts" },
              ...buildGlItems(glAccounts, { visibleOnly: true, selectedIds: [value.gl_account_id] }),
            ]}
          />
        </div>
      )}
      {show.includes("card") && (
        <div className="w-[150px]">
          <SearchableSelect
            testId="filter-card"
            size="sm"
            value={value.card_last_four || ""}
            onChange={(v) => update("card_last_four", v)}
            placeholder="Card"
            searchPlaceholder="Search cards..."
            emptyText="No cards."
            items={[
              { value: "", label: "All cards" },
              ...allCards.map((c) => ({
                value: c.last_four,
                label: `****${c.last_four}`,
                searchText: c.holder || "",
                suffix: c.holder ? <span className="text-[9px] text-zinc-400 ml-2 truncate">{c.holder}</span> : null,
              })),
            ]}
          />
        </div>
      )}
      {hasAny && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onChange({})}
          data-testid="filter-clear-btn"
          className="h-8 px-2 text-xs text-zinc-500 hover:text-zinc-700 gap-1"
        >
          <X size={11} /> Clear
        </Button>
      )}
    </div>
  );
}
